<script lang="ts">
	// Svelte
	import { getContext, onDestroy, onMount } from 'svelte';
	// Formatters
	import { format } from 'svelte-i18n';
	// Helpers
	import { real_id } from '../../../helpers/interface_helpers';
	import { onSettingsResponse } from '../../../helpers/device_settings_helpers';
	import { capFirstChar } from '../../../helpers/utilities';
	import {
		computeHa1,
		verifyPassphrase,
		resolveDeviceRealm,
		DEVICE_AUTH_USER,
	} from '../../../helpers/device_auth_helpers';
	import { setSidePanelState } from 'src/components/Sidepanel/sidepanel-helpers';
	import { resetDrawerClose } from '../../../helpers/drawer_helpers';
	// Services
	import { gen2Request } from '../../../services/gen_2_http_reqs';
	// Stores
	import type { Writable } from 'svelte/store';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';
	import { devices_settings, device_auths, auth_locked_devices } from '../../../stores';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	// Design system components
	import {
		Alert,
		Button,
		Checkbox,
		Container,
		Input,
		Modal,
		Text,
	} from '@Shelly/shelly-design-system';
	// Components
	import NetworkModal from '../NetworkComponents/NetworkModal.svelte';
	// Types
	import type { SettingRequest } from '../../../types/requests/settings/setting';
	import type { Device } from '../../../types/Device';

	export let device: Device;
	export let isOffline = false;

	const id = real_id(device.id);

	$: settings = $devices_settings[id];

	// Authoritative auth state. We do NOT bind this to the cloud-cached `DeviceInfo.auth_en`
	// (which lags after a change); instead we read it live from the device on mount/after save.
	let authEnabled = $devices_settings[id]?.DeviceInfo?.auth_en === true;
	// Toggle state, kept in sync with the authoritative state until the user changes it.
	let enabled = authEnabled;
	let password = '';
	let validationError = '';
	let waitForResponse = false;

	/**
	 * Reads the live auth-enabled state straight from the device (auth-exempt RPC that reaches
	 * the device over the cloud websocket or LAN — not the lagging cloud settings cache) and
	 * reconciles the toggle + the cached store value.
	 */
	const refreshAuthState = async () => {
		const res: any = await gen2Request(device, 'Shelly.GetDeviceInfo', {});
		if (!res?.isok) return;
		const liveAuthEn = (res?.auth_en ?? res?.data?.auth_en) === true;
		authEnabled = liveAuthEn;
		enabled = liveAuthEn;
		const current = $devices_settings[id];
		devices_settings.update_devices_settings(id, {
			...current,
			DeviceInfo: { ...current?.DeviceInfo, auth_en: liveAuthEn },
		});
	};

	onMount(refreshAuthState);

	// Modals
	let enableModal: NetworkModal; // lockout warning before enabling/changing
	let showDisableModal = false;
	let disablePassword = '';
	let disableError = '';

	/** Resolve the realm (device id) required to compute a device-valid ha1. */
	const ensureRealm = async (): Promise<string> => {
		const cached = $device_auths[id];
		let realm = resolveDeviceRealm(settings?.DeviceInfo?.id, cached?.auth_data);
		if (realm) return realm;
		// Fall back to a live identity probe so we never store an ha1 bound to the wrong realm.
		const res: any = await gen2Request(device, 'Shelly.GetDeviceInfo', {});
		realm = res?.isok ? (res?.id ?? res?.data?.id ?? '') : '';
		return realm;
	};

	/** No spaces allowed (mirrors the embedded web validation). */
	const validatePassword = (): boolean => {
		if (!password) {
			validationError = $format('enter_password', { default: 'Please enter a password' });
			return false;
		}
		if (/\s/.test(password)) {
			validationError = $format('auth_no_spaces_allowed', {
				default: 'Empty spaces are not allowed',
			});
			return false;
		}
		validationError = '';
		return true;
	};

	/** Close the whole device view so the existing lock/state logic re-renders cleanly. */
	const closeDeviceView = () => {
		if ($showNewLayout) {
			setSidePanelState({ showSettingsModal: false });
		} else {
			resetDrawerClose();
		}
	};

	const applySetAuth = async (ha1: string | null) => {
		waitForResponse = true;
		const realm = await ensureRealm();
		if (!realm) {
			waitForResponse = false;
			onSettingsResponse({ isok: false } as SettingRequest, device, {
				danger: $format('auth_realm_unavailable', {
					default:
						'Could not determine the device identity. Try again when the device is reachable.',
				}),
			});
			return;
		}

		const params = { user: DEVICE_AUTH_USER, realm, ha1 };
		const res: SettingRequest = await gen2Request(device, 'Shelly.SetAuth', params);

		const handled = onSettingsResponse(res, device, {
			success:
				ha1 === null
					? $format('auth_disabled_scc_toast', {
							default: 'Password protection turned off.',
						})
					: $format('auth_enabled_scc_toast', {
							default: 'Password protection saved.',
						}),
		});

		if (handled?.isok) {
			const nowEnabled = ha1 !== null;
			authEnabled = nowEnabled;
			enabled = nowEnabled;

			const updated = {
				...settings,
				DeviceInfo: { ...settings?.DeviceInfo, auth_en: nowEnabled },
			};
			devices_settings.update_devices_settings(id, updated);

			if (ha1 === null) {
				// Protection off. Clear the locked state, but intentionally KEEP the cached
				// credential for now: the cloud settings cache still reports auth_en=true for a
				// few seconds, and the device-state stream would otherwise re-lock the device
				// (auth_en === true && no credentials). Every re-lock path short-circuits on
				// `hasCredentials`, so keeping the ha1 prevents the spurious lock. It is removed
				// automatically once auth_en=false propagates (v2_device_state / status sync).
				auth_locked_devices.remove(id);
			} else {
				// Cache the non-reversible ha1 (FE-1998) so the user is not forced to re-enter the
				// password they just set: cloud requests are auth-exempt and the next local request
				// auto-authenticates via the 401 → retry path using this ha1.
				const existing = $device_auths[id] ?? {};
				device_auths.update(id, { ...existing, ha1, nc: 1 });
				auth_locked_devices.remove(id);
			}

			password = '';
			closeDeviceView();
		}

		waitForResponse = false;
	};

	/** Entry point for the Save action (sticky bottom in new UI / inline button in old UI). */
	const handleSave = () => {
		validationError = '';

		// Turning protection OFF.
		if (authEnabled && !enabled) {
			disablePassword = '';
			disableError = '';
			showDisableModal = true;
			return;
		}

		// Enabling or changing the password.
		if (enabled) {
			if (authEnabled && !password) {
				// Nothing to change — guard against a no-op save.
				validationError = $format('auth_enter_new_to_change', {
					default: 'Enter a new password to change it.',
				});
				return;
			}
			if (!validatePassword()) return;
			enableModal.open();
		}
	};

	const confirmEnable = async () => {
		const ha1 = computeHa1(await ensureRealm(), password);
		if (!ha1) {
			onSettingsResponse({ isok: false } as SettingRequest, device, {
				danger: $format('auth_realm_unavailable', {
					default:
						'Could not determine the device identity. Try again when the device is reachable.',
				}),
			});
			return;
		}
		await applySetAuth(ha1);
	};

	const confirmDisable = async () => {
		disableError = '';
		const cached = $device_auths[id];
		const realm = resolveDeviceRealm(settings?.DeviceInfo?.id, cached?.auth_data);

		// Always require the current passphrase to turn protection off.
		if (!disablePassword) {
			disableError = $format('enter_password', { default: 'Please enter a password' });
			return;
		}

		// Re-verify against the cached credential. This is a pure local comparison
		// (recompute ha1 and match), so it works on both the local and cloud channels.
		if (cached?.ha1 && !verifyPassphrase(realm, disablePassword, cached.ha1)) {
			disableError = $format('auth_incorrect_passphrase', {
				default: 'Incorrect passphrase.',
			});
			return;
		}

		showDisableModal = false;
		await applySetAuth(null);
	};

	// New-UI sticky bottom Save button.
	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	$: if (stickyBottomAction) {
		if ($showNewLayout) {
			stickyBottomAction.set({
				onClick: handleSave,
				loading: waitForResponse,
				disabled: isOffline,
			});
		} else {
			stickyBottomAction.set(null);
		}
	}

	onDestroy(() => {
		stickyBottomAction?.set(null);
	});
</script>

<Text styleType="body-small" class="mb-s">
	{$format('auth_section_description', { default: 'Limit access to your device' })}
</Text>

<Checkbox
	size="l"
	class="mb-s"
	bind:checked={enabled}
	disabled={isOffline}
	info={$showNewLayout
		? $format('auth_checkbox_description', {
				default: 'Require a password to access this device over the local network.',
			})
		: undefined}
	id="checkbox-device-auth-enable"
>
	{capFirstChar($format('auth_password_protected_device', { default: 'Password protected device' }))}
</Checkbox>

{#if !$showNewLayout}
	<Alert
		class="mb-s"
		heading={$format('auth_checkbox_description', {
			default: 'Require a password to access this device over the local network.',
		})}
	/>
{/if}

<hr class="mb-s" />

<Input
	a11yProps={{
		iconButtonAriaLabel: capFirstChar($format('label_password', { default: 'password' })),
	}}
	size="m"
	label={capFirstChar($format('label_password', { default: 'Password' }))}
	placeholder={authEnabled && !password
		? '••••••••'
		: $format('auth_password_placeholder', { default: 'Set or change device password' })}
	bind:value={password}
	disabled={!enabled || isOffline}
	error={validationError}
	type="password"
	icon="fas fa-lock-keyhole"
	autocomplete="new-password"
	on:input={() => (validationError = '')}
	on:keydown={(e) => {
		if (e.detail?.keyCode === 13) handleSave();
	}}
	id="input-device-auth-password"
	aria-labelledby="input-device-auth-password"
/>

{#if authEnabled}
	<Alert
		type="info"
		class="mt-s"
		heading={$format('auth_already_enabled_hint', {
			default:
				'Protection is on. Enter a new password to change it, or turn off the toggle to remove protection.',
		})}
	/>
{/if}

{#if !$showNewLayout}
	<Button size="m" width="full" on:click={handleSave} loading={waitForResponse} disabled={isOffline}>
		{$format('button_save', { default: 'Save' })}
	</Button>
{/if}

<!-- Lockout warning + confirmation before enabling/changing the password -->
<NetworkModal
	title={authEnabled
		? $format('auth_change_password_title', { default: 'Change device password?' })
		: $format('auth_enable_title', { default: 'Enable password protection?' })}
	bind:this={enableModal}
	on:confirm={confirmEnable}
	{isOffline}
	btnTexts={{
		cancelText: '',
		saveText: authEnabled
			? $format('button_save', { default: 'Save' })
			: $format('label_enable', { default: 'Enable' }),
	}}
>
	{$format('auth_lockout_warning', {
		default:
			'Keep this password safe. If you forget it, the only way to regain full control of the device is a factory reset — there is no on-device recovery.',
	})}
</NetworkModal>

<!-- Turn-off confirmation with current-passphrase re-verification -->
{#if showDisableModal}
	<Modal
		showModal={true}
		on:close={() => (showDisableModal = false)}
		title={$format('auth_disable_title', { default: 'Turn off protection?' })}
	>
		<Container slot="content" padding="0">
			<Text styleType="body-small" class="mb-m">
				{$format('auth_disable_description', {
					default: 'Confirm your current device password to turn off protection.',
				})}
			</Text>
			<Input
				a11yProps={{
					iconButtonAriaLabel: capFirstChar(
						$format('label_password', { default: 'password' }),
					),
				}}
				size="m"
				label={capFirstChar($format('label_password', { default: 'Password' }))}
				bind:value={disablePassword}
				type="password"
				icon="fas fa-lock-keyhole"
				autocomplete="off"
				error={disableError}
				on:input={() => (disableError = '')}
				on:keydown={(e) => {
					if (e.detail?.keyCode === 13) confirmDisable();
				}}
				id="input-device-auth-disable"
				aria-labelledby="input-device-auth-disable"
			/>
		</Container>
		<div slot="footer" class="d-flex justify-content-end flex-wrap gap-s">
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
				responsiveSize={{ sm: 'm' }}
				variant="secondary"
				on:click={() => (showDisableModal = false)}
			>
				{capFirstChar($format('button_cancel', { default: 'Cancel' }))}
			</Button>
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
				responsiveSize={{ sm: 'm' }}
				variant="danger"
				loading={waitForResponse}
				on:click={confirmDisable}
			>
				{capFirstChar($format('button_disable', { default: 'Disable' }))}
			</Button>
		</div>
	</Modal>
{/if}
